import { z } from 'zod';
import type { LanguageCode } from './lang.js';

/**
 * Contracts of the two pages that need no login: a job's apply page, where a candidate adds
 * themselves, and the read-only report a recruiter shares with a hiring manager.
 */

/** What a candidate types on the apply page. The resume comes as a file beside these fields. */
export const ApplySchema = z.object({
  name: z.string().trim().min(2, 'Please give your name').max(120),
  phone: z.string().trim().min(6, 'Please give a phone number').max(30),
  email: z.string().trim().max(200).optional(),
  /** Years of experience, as typed ("2", "2.5", "fresher"). */
  experience: z.string().trim().max(40).optional(),
  /** Current role or industry. */
  sector: z.string().trim().max(120).optional(),
  location: z.string().trim().max(120).optional(),
  /** The candidate agrees to an AI interview that is recorded and assessed. */
  consent: z.literal('yes', { message: 'Please agree to the interview terms' }),
  /** A honeypot: people never see it, bots fill it. */
  website: z.string().max(0).optional(),
});
export type ApplyInput = z.infer<typeof ApplySchema>;

export interface PublicJobInfo {
  jobTitle: string;
  company: string;
  location: string;
  workMode: string;
  /** What the interviewer may say about the role: shown on the page too. */
  description: string;
  minutes: number;
  languages: Array<{ code: LanguageCode; label: string; native: string }>;
  /** How an applicant is interviewed: a link to start in the browser, or a phone call. */
  how: 'link' | 'call';
  /** The job is active, so applying starts the process. */
  accepting: boolean;
  /** Something the company wants said on the page. */
  note: string | null;
  /** The number candidates can call to take the interview, when phone calls are set up. */
  callIn: string | null;
}

export interface ApplyResult {
  candidateId: string;
  /** link: start now in the browser. call: the AI will call. queued: a recruiter starts it. already: applied before. */
  outcome: 'link' | 'call' | 'queued' | 'already';
  link: string | null;
  callIn: string | null;
  message: string;
}

/** A candidate's results, as a hiring manager without a login sees them. No phone, no email. */
export interface CandidateReport {
  candidate: { name: string; experience: string | null; sector: string | null; location: string | null };
  job: { title: string; company: string; location: string };
  status: string;
  stages: ReportStage[];
  decisions: Array<{ kind: string; reason: string | null; by: string; at: string }>;
  generatedAt: string;
  expiresAt: string;
}

export interface ReportStage {
  stage: 'screening' | 'assessment' | 'recruiter';
  label: string;
  /** What happened, in words: "Completed", "Booked for …", "Not started". */
  status: string;
  score: number | null;
  verdict: 'QUALIFIED' | 'NEEDS_REVIEW' | 'NOT_QUALIFIED' | null;
  recommendation: string | null;
  summary: string | null;
  strengths: string[];
  concerns: string[];
  redFlags: string[];
  competencies: Array<{ label: string; weight: number; score: number | null; rationale: string; evidence: string[] }>;
  facts: Array<{ label: string; value: string; rule: 'passed' | 'failed' | null; message: string | null }>;
  tasks: Array<{ title: string; kind: string; score: number | null; rationale: string }>;
}
